// src/PublicUser/components/FreeCertificates.jsx
import React, { useState } from 'react';
import FreeCertificatesHero from './FreeCertificatesHero';
import { freeCertificatesData, getCertCategories, getCertStats } from '../../data/FreeCertificatesData';
import '../../css/certification/FreeCertificates.css';

const FreeCertificates = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...getCertCategories()];
  const stats = getCertStats();

  // Filter certificates based on search and category
  const filteredCertificates = freeCertificatesData.filter(cert => {
    const matchesSearch = cert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          cert.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          cert.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          cert.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All' || cert.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedByCategory = filteredCertificates.reduce((acc, cert) => {
    if (!acc[cert.category]) {
      acc[cert.category] = [];
    }
    acc[cert.category].push(cert);
    return acc;
  }, {});

  // Render stars based on rating
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    
    for (let i = 0; i < fullStars; i++) {
      stars.push('★');
    }
    if (hasHalfStar) {
      stars.push('½');
    }
    
    return stars.join('');
  };

  return (
    <div className="ts-cert-wrapper">
      <FreeCertificatesHero 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        stats={stats}
      />

      {/* Category Filter */}
      <div className="ts-cert-filter-wrapper">
        <div className="ts-cert-filter-section">
          <div className="ts-cert-track">
            {categories.map(category => (
              <button
                key={category}
                className={`ts-cert-btn ${selectedCategory === category ? 'ts-active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="ts-cert-content">
        {Object.keys(groupedByCategory).length === 0 ? (
          <div className="ts-cert-empty-state">
            <p>🔍 No certificates found matching your search.</p>
            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>Try adjusting your search or category filter.</p>
          </div>
        ) : (
          Object.entries(groupedByCategory).map(([category, certificates]) => (
            <div key={category} className="ts-cert-category-group">
              <div className="ts-cert-section-header">
                <span>{category === 'AI/ML' ? '🤖' : 
                         category === 'Data Science' ? '📊' :
                         category === 'Web Development' ? '💻' :
                         category === 'Cloud Computing' ? '☁️' :
                         category === 'IT & Cybersecurity' ? '🔒' :
                         category === 'Programming' ? '🐍' : '📚'}</span>
                {category} ({certificates.length})
              </div>

              <div className="ts-cert-grid">
                {certificates.map(cert => (
                  <div key={cert.id} className="ts-cert-card">
                    <div className="ts-cert-card-top">
                      <div className="ts-cert-icon-box">
                        {cert.icon}
                      </div>
                      <div className="ts-cert-info-block">
                        <h3>{cert.name}</h3>
                        <div className="ts-cert-provider">
                          <span>{cert.provider}</span>
                          <span className="ts-cert-platform">{cert.platform}</span>
                        </div>
                      </div>
                    </div>

                    <p className="ts-cert-desc">{cert.description}</p>

                    <div className="ts-cert-meta">
                      <span className="ts-cert-meta-item">⏱️ {cert.duration}</span>
                      <span className="ts-cert-meta-item">📚 {cert.level}</span>
                    </div>

                    <div className="ts-cert-tags">
                      {cert.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="ts-cert-tag">#{tag}</span>
                      ))}
                    </div>

                    <div className="ts-cert-card-bottom">
                      <div>
                        {cert.isFree && (
                          <span className="ts-cert-badge free">✅ Free</span>
                        )}
                        {cert.hasFinancialAid && (
                          <span className="ts-cert-badge financial-aid">💰 Financial Aid</span>
                        )}
                      </div>
                      <div className="ts-cert-rating">
                        {renderStars(cert.rating)} 
                        <span>({cert.rating})</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                      <div className="ts-cert-students">
                        👥 {cert.students} students
                      </div>
                      <a 
                        href={cert.certificateLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ts-cert-link"
                      >
                        Enroll Now →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FreeCertificates;